import { useMemo, useState } from 'react';
import { formatCurrency } from '@/lib/helpers/format-currency';
import type { ReferralListItem, ReferralStatus } from '@/types/referral';
import { ChevronRight, Download, Filter, Send, Share2 } from 'lucide-react';
import {
  formatReferralDate,
  referralAvatarColor,
  referralInitials,
} from '../utils';
import { ReferralStatusBadge } from './referral-status-badge';

type ReferralFilter = 'all' | 'active' | 'paid' | 'expired';

const ACTIVE_STATUSES: ReferralStatus[] = [
  'SIGNED_UP',
  'CHANNEL_CONNECTED',
  'QUALIFIED',
  'ACTIVE',
];

interface ReferralsTableProps {
  referrals: ReferralListItem[];
  onShare: () => void;
}

function progressLabel(referral: ReferralListItem) {
  switch (referral.status) {
    case 'QUALIFIED':
      return referral.monthIndicator ?? 'Qualifying';
    case 'ACTIVE':
      return 'Reward unlocked';
    case 'COMPLETED':
      return 'Paid out';
    case 'EXPIRED':
      return 'Window closed';
    case 'CHANNEL_CONNECTED':
      return 'Channel connected';
    default:
      return 'Awaiting first sale';
  }
}

export const ReferralsTable = ({ referrals, onShare }: ReferralsTableProps) => {
  const [tab, setTab] = useState<ReferralFilter>('all');

  const counts = useMemo(() => {
    const active = referrals.filter((referral) =>
      ACTIVE_STATUSES.includes(referral.status)
    ).length;
    const paid = referrals.filter(
      (referral) => referral.status === 'COMPLETED'
    ).length;
    const expired = referrals.filter(
      (referral) => referral.status === 'EXPIRED'
    ).length;

    return {
      all: referrals.length,
      active,
      paid,
      expired,
    };
  }, [referrals]);

  const filtered = useMemo(() => {
    if (tab === 'active') {
      return referrals.filter((referral) =>
        ACTIVE_STATUSES.includes(referral.status)
      );
    }
    if (tab === 'paid') {
      return referrals.filter((referral) => referral.status === 'COMPLETED');
    }
    if (tab === 'expired') {
      return referrals.filter((referral) => referral.status === 'EXPIRED');
    }
    return referrals;
  }, [referrals, tab]);

  if (referrals.length === 0) {
    return (
      <div className="empty-card">
        <div className="empty-ring" style={{ margin: '0 auto 18px' }}>
          <Send className="size-10" />
          <span className="badge">₦</span>
        </div>
        <h2 className="rne-h2">No referrals yet</h2>
        <p
          className="rne-meta"
          style={{ maxWidth: 480, margin: '8px auto 22px' }}
        >
          Send your link to vendors you know on WhatsApp. ₦500 lands here the
          moment one of them makes their first sale, then up to ₦1,500/month for
          6 months.
        </p>
        <div className="empty-actions">
          <button type="button" className="btn-primary-rne" onClick={onShare}>
            <Share2 className="size-4" />
            Share my referral link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="table-card">
      <div className="table-h">
        <div className="tabs">
          <button
            type="button"
            className={tab === 'all' ? 'active' : ''}
            onClick={() => setTab('all')}
          >
            All ({counts.all})
          </button>
          <button
            type="button"
            className={tab === 'active' ? 'active' : ''}
            onClick={() => setTab('active')}
          >
            Active ({counts.active})
          </button>
          <button
            type="button"
            className={tab === 'paid' ? 'active' : ''}
            onClick={() => setTab('paid')}
          >
            Paid ({counts.paid})
          </button>
          <button
            type="button"
            className={tab === 'expired' ? 'active' : ''}
            onClick={() => setTab('expired')}
          >
            Expired ({counts.expired})
          </button>
        </div>
        <div className="tools">
          <button type="button" className="btn-outline-rne btn-sm">
            <Filter className="size-3.5" />
            Filter
          </button>
          <button type="button" className="btn-outline-rne btn-sm">
            <Download className="size-3.5" />
            Export
          </button>
        </div>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th>Friend</th>
            <th className="col-hide-sm">Status</th>
            <th className="col-hide-sm">Joined</th>
            <th className="col-hide-sm">Progress</th>
            <th className="right">Earned</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filtered.map((referral) => (
            <tr key={referral.id} className="clickable">
              <td>
                <div className="nm-c">
                  <div
                    className="av av-40"
                    style={{ background: referralAvatarColor(referral.name) }}
                  >
                    {referralInitials(referral.name)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700 }}>{referral.name}</div>
                  </div>
                </div>
              </td>
              <td className="col-hide-sm">
                <ReferralStatusBadge status={referral.status} />
              </td>
              <td className="col-hide-sm rne-meta">
                {formatReferralDate(referral.signupDate)}
              </td>
              <td className="col-hide-sm rne-meta">
                {progressLabel(referral)}
              </td>
              <td className="right">
                <span
                  className="num-c"
                  style={{
                    color: referral.totalEarnings ? '#18854F' : '#6B7280',
                  }}
                >
                  {referral.totalEarnings
                    ? `+${formatCurrency(referral.totalEarnings)}`
                    : '—'}
                </span>
              </td>
              <td className="right" style={{ width: 36 }}>
                <ChevronRight className="size-4 text-muted-foreground" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className="empty-frame">
          <div
            className="empty-ring"
            style={{
              width: 72,
              height: 72,
              fontSize: 36,
              margin: '0 auto 12px',
            }}
          >
            <Filter className="size-8" />
          </div>
          <h3 className="rne-h3">Nothing in this tab yet</h3>
          <p className="rne-meta" style={{ marginTop: 6 }}>
            Try a different filter, or invite a few more friends.
          </p>
        </div>
      )}
    </div>
  );
};
